import {Injectable, OnDestroy} from '@angular/core';
import {Block} from '@paperlessio/sdk/api/models';
import {BlockStore} from '@blocks/block/block.store';
import {interval, Observable, of, Subject, Subscription} from 'rxjs';
import {BlockMutationCollection, BlockMutationResult} from '@blocks/block/block-mutation.type';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {concatMap, first, groupBy, map, mergeAll, tap, throttle,} from 'rxjs/operators';

/**
 * Proxy service to the mutation methods of the block store.
 * Trigger change detection cycles manually for the blocks
 * which use the OnPush change detection strategy
 */
@Injectable({
  providedIn: 'root'
})
export class BlockMutationService implements OnDestroy {
  private readonly debounceTimeMs = 500;
  private subs: Subscription = new Subscription();

  private updateBlockMutationSubject = new Subject<{block: Block, forceUpdate: boolean}>();
  private blockMutationResultSubject = new Subject<BlockMutationResult>();

  private transactionMutationCollection: BlockMutationCollection;

  constructor(
    private blockStore: BlockStore,
    private blockChangeDetectionService: BlockChangeDetectionService
  ) {
    this.subscribeToUpdateBlockMutation();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  // Dispatch updated block ids locally for the manual change detection
  // Add the updated block to the processing chain
  updateBlock(block, forceUpdate: boolean = false): Observable<BlockMutationResult> {
    this.blockChangeDetectionService.setChangedBlockIds([block.id]);
    this.updateBlockMutationSubject.next({block, forceUpdate});
    return this.blockMutationResultSubject.pipe(first());
  }

  // Begin transaction
  public begin(bucketType: string, bucketId: number): BlockMutationCollection {
    return this.transactionMutationCollection = new BlockMutationCollection(bucketType, bucketId);
  }

  public addBlockUpdate(block: Block): BlockMutationCollection {
    if (!this.transactionMutationCollection) {
      throw new Error('addBlockUpdate can only be called when begin has been called before and a transactionMutationCollection exists.');
    }

    return this.transactionMutationCollection.updateBlock(block);
  }

  public addBlockCreate(block: Block, parent: Block, position: number): BlockMutationCollection {
    if (!this.transactionMutationCollection) {
      throw new Error('addBlockCreate can only be called when begin has been called before and a transactionMutationCollection exists.');
    }

    return this.transactionMutationCollection.createBlock(block, parent, position);
  }

  public addBlockDelete(block: Block): BlockMutationCollection {
    if (!this.transactionMutationCollection) {
      throw new Error('addBlockDelete can only be called when begin has been called before and a transactionMutationCollection exists.');
    }

    return this.transactionMutationCollection.deleteBlock(block);
  }

  public commit(): Observable<BlockMutationResult> {
    if (!this.transactionMutationCollection) {
      return;
    }

    const mutationCollectionCopy = JSON.parse(JSON.stringify(this.transactionMutationCollection));
    this.transactionMutationCollection = null;

    return this.blockStore.sendAndUpdate(mutationCollectionCopy).pipe(tap(result => {
      this.blockChangeDetectionService.setChangedBlockIds(result.updated_blocks.map(b => b.id));
    }));
  }

  // Debounce the call to the block store
  private subscribeToUpdateBlockMutation() {
    this.subs.add(
      this.updateBlockMutationSubject.pipe(
        groupBy(mutation => mutation.block.id),
        map(group => group.pipe(
          // Report the first and the last observation. See https://stackoverflow.com/a/57097217
          throttle(ev => interval(this.debounceTimeMs), {leading: true, trailing: true})
        )),
        mergeAll(),
        concatMap(mutation => of(mutation))
      ).subscribe((mutation) => {
        this.blockStore.updateBlock(mutation.block, mutation.forceUpdate)
          .subscribe((blockMutationResult: BlockMutationResult) => {
            this.blockMutationResultSubject.next(blockMutationResult)
          });
      })
    );
  }
}
